import { onAuthStateChanged } from "firebase/auth";
import { openRuntimeFirestore } from "../runtime/runtimeConnectionService";
import { getRuntimeInstance } from "../runtime/connectionRuntimeService";
import { resolveConnectionRuntimeConfig } from "../runtime/connectionRuntimeConfigService";
import { makeServiceResult } from "./connectionVerifyUtils";
import {
  AUTH_RUNTIME_MISMATCH_MESSAGE,
  AUTH_SESSION_NOT_FOUND_MESSAGE,
  FIREBASE_AUTH_INACTIVE_MESSAGE,
  HEALTH_CHECK_MODE_AUTHENTICATED,
  HEALTH_CHECK_MODE_CONNECTION_ONLY,
  RUNTIME_MISMATCH_MESSAGE,
} from "./firestoreTestMessages";

/** Last Deep Test runtime + auth references — Health Check aligns to these when needed. */
let deepTestRuntimeSnapshot = null;
let deepTestAuthSnapshot = null;
let deepTestRuntimeRef = null;

const AUTH_COMPARE_KEYS = [
  "runtimeAppName",
  "projectId",
  "connectionId",
  "authInstanceId",
  "uid",
  "email",
  "provider",
  "anonymous",
];

function isDevBuild() {
  return process.env.NODE_ENV !== "production";
}

function devLog(...args) {
  if (!isDevBuild()) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}

function suiteLabel(suite) {
  return suite === "deep_test" ? "DEEP TEST DEBUG" : "HEALTH CHECK DEBUG";
}

export function buildConnectionTestRuntimeSnapshot(runtime, suite) {
  const app = runtime.app;
  const auth = runtime.auth;
  const user = auth?.currentUser ?? null;
  const firestoreAppName = runtime.firestore?.app?.name ?? app?.name ?? null;
  const authAppName = auth?.app?.name ?? app?.name ?? null;

  return {
    suite,
    connectionId: runtime.connectionId ?? null,
    projectId: runtime.projectId ?? app?.options?.projectId ?? null,
    runtimeAppName: app?.name ?? null,
    firestoreAppName,
    authAppName,
    uid: user?.uid ?? null,
    email: user?.email ?? null,
    authenticated: Boolean(user),
    runtimeInstanceId: runtime.instanceId ?? app?.name ?? null,
  };
}

export function buildAuthRuntimeSnapshot(runtime, auth, authStateReady = false) {
  const app = runtime.app;
  const user = auth?.currentUser ?? null;
  const provider = user?.providerData?.[0]?.providerId ?? (user?.isAnonymous ? "anonymous" : null);

  return {
    runtimeAppName: app?.name ?? null,
    projectId: runtime.projectId ?? app?.options?.projectId ?? null,
    connectionId: runtime.connectionId ?? null,
    authInstanceId: auth?.app?.name ?? app?.name ?? null,
    currentUser: user,
    uid: user?.uid ?? null,
    email: user?.email ?? null,
    provider,
    anonymous: Boolean(user?.isAnonymous),
    authStateReady,
  };
}

function snapshotFieldsMatch(a, b) {
  const keys = [
    "connectionId",
    "projectId",
    "runtimeAppName",
    "firestoreAppName",
    "authAppName",
    "uid",
    "email",
    "authenticated",
    "runtimeInstanceId",
  ];
  return keys.every((key) => a[key] === b[key]);
}

export function authSnapshotsMatch(a, b) {
  if (!a || !b) return true;
  return AUTH_COMPARE_KEYS.every((key) => a[key] === b[key]);
}

export function logConnectionTestRuntimeDebug(snapshot, suite) {
  if (!isDevBuild()) return;

  devLog("==========================");
  devLog(suiteLabel(suite));
  devLog("==========================");
  devLog("Connection ID:", snapshot.connectionId);
  devLog("Project ID:", snapshot.projectId);
  devLog("Firebase App Name:", snapshot.runtimeAppName);
  devLog("Firestore App Name:", snapshot.firestoreAppName);
  devLog("Auth App Name:", snapshot.authAppName);
  devLog("Current User UID:", snapshot.uid);
  devLog("Current User Email:", snapshot.email);
  devLog("Authenticated:", snapshot.authenticated);
  devLog("Runtime Instance ID:", snapshot.runtimeInstanceId);
}

export function logAuthRuntimeDebug(authSnapshot, suite) {
  if (!isDevBuild()) return;

  const header = suite === "deep_test" ? "DEEP TEST AUTH" : "HEALTH CHECK AUTH";
  devLog(`===== ${header} =====`);
  devLog("Runtime App:", authSnapshot.runtimeAppName);
  devLog("Project ID:", authSnapshot.projectId);
  devLog("Connection ID:", authSnapshot.connectionId);
  devLog("Auth Instance ID:", authSnapshot.authInstanceId);
  devLog("Current User:", authSnapshot.currentUser);
  devLog("UID:", authSnapshot.uid);
  devLog("Email:", authSnapshot.email);
  devLog("Provider:", authSnapshot.provider);
  devLog("Anonymous:", authSnapshot.anonymous);
  devLog("Auth State Ready:", authSnapshot.authStateReady);
  devLog("=============================");
}

export function logFirestoreOperationResult(suite, label, result) {
  if (!isDevBuild()) return;
  devLog(`${suiteLabel(suite)} — ${label}:`, result);
}

/** Wait for Firebase Auth to finish restoring persisted session state. */
export async function waitForRuntimeAuth(auth, timeoutMs = 10_000) {
  if (!auth) return null;

  if (typeof auth.authStateReady === "function") {
    await Promise.race([
      auth.authStateReady(),
      new Promise((_, reject) => {
        setTimeout(() => reject(new Error("Auth state ready timed out")), timeoutMs);
      }),
    ]).catch(() => {});
    return auth.currentUser;
  }

  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      unsub();
      resolve(auth.currentUser);
    }, timeoutMs);

    const unsub = onAuthStateChanged(auth, (user) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      resolve(user);
    });
  });
}

function applyRuntimeBindings(target, source) {
  target.app = source.app;
  target.firestore = source.firestore;
  target.auth = source.auth;
  target.storage = source.storage;
  target.functions = source.functions;
  target.connection = source.connection;
  target.connectionId = source.connectionId;
  target.projectId = source.projectId;
  target.instanceId = source.instanceId;
  return target;
}

async function prepareRuntimeAuthentication(runtime, suite) {
  let auth = runtime.auth;
  let authStateReady = false;

  if (auth) {
    await waitForRuntimeAuth(auth);
    authStateReady = true;
  }

  let authSnapshot = buildAuthRuntimeSnapshot(runtime, auth, authStateReady);
  logAuthRuntimeDebug(authSnapshot, suite);

  if (suite === "deep_test") {
    deepTestAuthSnapshot = authSnapshot;
    deepTestRuntimeRef = {
      app: runtime.app,
      firestore: runtime.firestore,
      auth: runtime.auth,
      storage: runtime.storage,
      functions: runtime.functions,
      connection: runtime.connection,
      connectionId: runtime.connectionId,
      projectId: runtime.projectId,
      instanceId: runtime.instanceId,
    };
  } else if (deepTestAuthSnapshot) {
    if (!authSnapshotsMatch(deepTestAuthSnapshot, authSnapshot)) {
      devLog(AUTH_RUNTIME_MISMATCH_MESSAGE);
    }

    const deepHasUser = Boolean(deepTestAuthSnapshot.uid);
    const healthHasUser = Boolean(authSnapshot.uid);

    if (!healthHasUser && deepHasUser && deepTestRuntimeRef) {
      applyRuntimeBindings(runtime, deepTestRuntimeRef);
      auth = runtime.auth;
      await waitForRuntimeAuth(auth);
      authStateReady = true;
      authSnapshot = buildAuthRuntimeSnapshot(runtime, auth, authStateReady);
      logAuthRuntimeDebug(authSnapshot, suite);

      if (!authSnapshotsMatch(deepTestAuthSnapshot, authSnapshot)) {
        devLog(AUTH_RUNTIME_MISMATCH_MESSAGE);
      }
    }
  }

  const sessionEstablished = Boolean(auth?.currentUser);
  runtime.auth = auth;
  runtime.authSnapshot = authSnapshot;
  runtime.authenticated = sessionEstablished;
  runtime.executionMode = sessionEstablished
    ? HEALTH_CHECK_MODE_AUTHENTICATED
    : HEALTH_CHECK_MODE_CONNECTION_ONLY;
  runtime.sessionEstablished = sessionEstablished;

  return authSnapshot;
}

async function openSharedRuntime(connectionId, connection) {
  await openRuntimeFirestore(connectionId);
  const instance = getRuntimeInstance(connectionId, connection);
  return {
    app: instance.app,
    firestore: instance.firestore,
    auth: instance.auth,
    storage: instance.storage,
    functions: instance.functions,
    connection,
    connectionId,
    projectId: connection.firebaseProjectId || instance.app?.options?.projectId || "",
    instanceId: instance.name,
  };
}

/**
 * Open the shared Studio runtime for Health Check / Deep Test.
 * Never initializes a new Firebase app — reuses the runtime cache used by Studio.
 */
export async function resolveConnectionTestRuntime(connectionOrId, { suite = "health_check" } = {}) {
  const connection = await resolveConnectionRuntimeConfig(connectionOrId);
  if (!connection?.id) {
    throw new Error("Connection id is required to use the shared connection runtime.");
  }

  let connectionId = connection.id;
  let runtime = await openSharedRuntime(connectionId, connection);

  let snapshot = buildConnectionTestRuntimeSnapshot(runtime, suite);
  logConnectionTestRuntimeDebug(snapshot, suite);

  if (suite === "deep_test") {
    deepTestRuntimeSnapshot = snapshot;
  } else if (deepTestRuntimeSnapshot && !snapshotFieldsMatch(deepTestRuntimeSnapshot, snapshot)) {
    devLog(RUNTIME_MISMATCH_MESSAGE);
    const alignedId = deepTestRuntimeSnapshot.connectionId || connectionId;
    if (alignedId !== connectionId) {
      const alignedConn = await resolveConnectionRuntimeConfig(alignedId);
      runtime = await openSharedRuntime(alignedId, alignedConn);
      connectionId = alignedId;
      snapshot = buildConnectionTestRuntimeSnapshot(runtime, suite);
      logConnectionTestRuntimeDebug(snapshot, suite);
    }
  }

  await prepareRuntimeAuthentication(runtime, suite);
  snapshot = buildConnectionTestRuntimeSnapshot(runtime, suite);

  if (suite === "deep_test") {
    deepTestRuntimeSnapshot = snapshot;
  }

  return {
    ...runtime,
    snapshot,
    authenticated: runtime.authenticated,
    executionMode: runtime.executionMode,
    sessionEstablished: runtime.sessionEstablished,
    suite,
  };
}

export function makeAuthInactiveFirestoreResult(latencyMs = 0) {
  return makeServiceResult("failed", FIREBASE_AUTH_INACTIVE_MESSAGE, latencyMs, { code: "unauthenticated" });
}

export function makeSessionNotFoundFirestoreResult(latencyMs = 0) {
  return makeServiceResult("failed", AUTH_SESSION_NOT_FOUND_MESSAGE, latencyMs, { code: "no-session" });
}

export function makeAuthInactivePermissions() {
  const inactive = {
    status: "failed",
    message: FIREBASE_AUTH_INACTIVE_MESSAGE,
    code: "unauthenticated",
  };
  return {
    read: { ...inactive },
    write: { status: "skipped", message: FIREBASE_AUTH_INACTIVE_MESSAGE, code: "unauthenticated" },
    delete: { status: "skipped", message: FIREBASE_AUTH_INACTIVE_MESSAGE, code: "unauthenticated" },
  };
}

export function makeSessionNotFoundPermissions(message = AUTH_SESSION_NOT_FOUND_MESSAGE) {
  const entry = { status: "failed", message, code: "no-session" };
  return {
    read: { ...entry },
    write: { status: "skipped", message, code: "no-session" },
    delete: { status: "skipped", message, code: "no-session" },
  };
}

/** @deprecated Use resolveConnectionTestRuntime — kept for callers importing openConnectionFirestore pattern. */
export async function openConnectionTestFirestore(connectionId) {
  return resolveConnectionTestRuntime(connectionId);
}

export function resetDeepTestRuntimeReference() {
  deepTestRuntimeSnapshot = null;
  deepTestAuthSnapshot = null;
  deepTestRuntimeRef = null;
}
